import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  FileText,
  Clock,
  User,
  Filter,
  Loader2,
  Activity,
  Eye,
  Edit,
  CheckCircle,
  Lock,
  FileSignature,
  AlertTriangle,
  Calendar,
  Globe,
  Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ICONS = {
  minutes_generated: FileText,
  minutes_edited: Edit,
  minutes_viewed: Eye,
  minutes_submitted_review: Clock,
  minutes_approved: CheckCircle,
  minutes_signed: FileSignature,
  minutes_locked: Lock,
  minutes_exported: FileText,
  minutes_restored: Activity,
  meeting_created: Calendar,
  meeting_updated: Edit,
  meeting_archived: FileText,
  meeting_deleted: AlertTriangle,
  resolution_created: CheckCircle,
  resolution_updated: Edit,
  declaration_created: Shield,
  declaration_updated: Edit,
  legal_hold_created: Lock,
  legal_hold_deactivated: Lock,
  document_accessed: Eye,
  transcript_accessed: Eye,
  evidence_accessed: Eye,
};

const EVENT_COLORS = {
  minutes_generated: "bg-blue-500/20 text-blue-400",
  minutes_edited: "bg-amber-500/20 text-amber-400",
  minutes_viewed: "bg-stone-500/20 text-stone-400",
  minutes_submitted_review: "bg-purple-500/20 text-purple-400",
  minutes_approved: "bg-emerald-500/20 text-emerald-400",
  minutes_signed: "bg-emerald-500/20 text-emerald-400",
  minutes_locked: "bg-red-500/20 text-red-400",
  minutes_exported: "bg-blue-500/20 text-blue-400",
  meeting_created: "bg-blue-500/20 text-blue-400",
  meeting_updated: "bg-amber-500/20 text-amber-400",
  resolution_created: "bg-emerald-500/20 text-emerald-400",
  declaration_created: "bg-purple-500/20 text-purple-400",
  legal_hold_created: "bg-red-500/20 text-red-400",
  legal_hold_deactivated: "bg-stone-500/20 text-stone-400",
};

const EVENT_TYPES = [
  { value: "all", label: "All Events" },
  { value: "minutes_generated", label: "Minutes Generated" },
  { value: "minutes_edited", label: "Minutes Edited" },
  { value: "minutes_submitted_review", label: "Submitted for Review" },
  { value: "minutes_approved", label: "Minutes Approved" },
  { value: "minutes_signed", label: "Minutes Signed" },
  { value: "minutes_locked", label: "Minutes Locked" },
  { value: "minutes_exported", label: "Minutes Exported" },
  { value: "meeting_created", label: "Meeting Created" },
  { value: "meeting_updated", label: "Meeting Updated" },
  { value: "resolution_created", label: "Resolution Created" },
  { value: "declaration_created", label: "Declaration Created" },
  { value: "legal_hold_created", label: "Legal Hold Created" },
  { value: "legal_hold_deactivated", label: "Legal Hold Deactivated" },
];

export default function AuditLogPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  
  const [eventType, setEventType] = useState("");
  const [entityType, setEntityType] = useState("");
  const [searchUserId, setSearchUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchAuditLog = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", limit);
      params.append("offset", offset);
      if (eventType) params.append("event_type", eventType);
      if (entityType) params.append("entity_type", entityType);
      if (searchUserId) params.append("user_id", searchUserId);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);

      const response = await axios.get(`${API}/company/audit?${params.toString()}`);
      setEvents(response.data.events || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, entityType, searchUserId, dateFrom, dateTo, offset]);

  const formatEventType = (type) => {
    return type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getEventIcon = (type) => {
    const Icon = EVENT_ICONS[type] || Activity;
    return Icon;
  };

  const getEventColor = (type) => {
    return EVENT_COLORS[type] || "bg-stone-500/20 text-stone-400";
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-stone-600"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-200" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-700 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-lg font-semibold text-stone-900">
                    Audit Log
                  </h1>
                  <p className="text-xs text-stone-500">
                    {total} total events
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-stone-200 p-4 mb-6" data-testid="audit-filters">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium text-stone-700">Filters</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Event Type</label>
              <Select value={eventType || "all"} onValueChange={(val) => { setEventType(val === "all" ? "" : val); setOffset(0); }}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Entity Type</label>
              <Select value={entityType} onValueChange={(val) => { setEntityType(val === "all" ? "" : val); setOffset(0); }}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="resolution">Resolution</SelectItem>
                  <SelectItem value="declaration">Declaration</SelectItem>
                  <SelectItem value="legal_hold">Legal Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-stone-500 mb-1 block">From Date</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                className="bg-white"
              />
            </div>
            
            <div>
              <label className="text-xs text-stone-500 mb-1 block">To Date</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                className="bg-white"
              />
            </div>
          </div>
        </div>

        {/* Audit Events List */}
        <div className="bg-white rounded-lg border border-stone-200" data-testid="audit-events-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary-700 animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No audit events found</p>
              <p className="text-sm text-stone-400 mt-1">Events will appear here as actions are taken</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-stone-100">
                {events.map((event) => {
                  const Icon = getEventIcon(event.event_type);
                  return (
                    <div
                      key={event.id}
                      className="p-4 hover:bg-stone-50 transition-colors"
                      data-testid={`audit-event-${event.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getEventColor(event.event_type)}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-stone-900">
                              {formatEventType(event.event_type)}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {event.entity_type}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {event.user_name || "System"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(event.created_at).toLocaleString()}
                            </span>
                            {event.ip_address && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {event.ip_address}
                              </span>
                            )}
                          </div>
                          
                          {event.metadata && Object.keys(event.metadata).length > 0 && (
                            <div className="mt-2 p-2 bg-stone-50 rounded text-xs text-stone-600 font-mono">
                              {Object.entries(event.metadata).map(([key, value]) => (
                                <div key={key}>
                                  <span className="text-stone-400">{key}:</span>{" "}
                                  <span>{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200">
                  <p className="text-sm text-stone-500">
                    Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} events
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                      disabled={offset === 0}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-stone-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOffset(offset + limit)}
                      disabled={offset + limit >= total}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
